// client/src/Router.js
import React, { Component } from 'react';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import PageTransition from './PageTransition';
import { SecureRoute, Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';

import Home from './pages/home/Home';
import homeWithData from './pages/home/homeWithData';

import genericBox from './pages/genericWithBoxes/generic';
import genericBoxWithData from './pages/genericWithBoxes/genericWithData';

import genericNews from './pages/genericwithnews/generic';
import genericNewsWithData from './pages/genericwithnews/genericWithData';

import list from './pages/genericList/list';
import listWithData from './pages/genericList/listWithData';

import news from './pages/news/news';
import newsPages from './pages/news/newsPages';
import newsPagesContentful from './pages/news/newsPagesContentful';
import newsPagesWithData from './pages/news/newsPagesWithData';
import newsChromeWithData from './pages/news/newsChromeWithData';
import newsWithData from './pages/news/newsWithData';
import newsTemplateWithData from './components/news/newsTemplateWithData';
import allNewsWithData from './components/news/newsTemplateWithData';

import executive from './pages/executive/executive';
import executiveWithData from './pages/executive/executiveWithData';

import search from './pages/search/SearchPage';
import searchWithData from './pages/search/searchWithData';

import calendar from './pages/calendar/calendar';
import calendarWithData from './pages/calendar/calendarWithData';

import gallery from './pages/gallery/gallery';
import galleryWithData from './pages/gallery/galleryWithData';

import signature from './pages/signature/signature';
import signatureWithData from './pages/signature/signatureWithData';

import PublicPage from './pages/publicPage/publicPage';
import publicPageWithData from './pages/publicPage/publicPageWithData';

// ------------------------------
// MODE / GROUP NORMALIZATION (Internal vs Seasonal)
// ------------------------------
const GROUPS = {
  INTERNAL_PROD: 'INTRANET_Internal',
  SEASONAL_PROD: 'INTRANET_Seasonal',
  INTERNAL_PREVIEW: 'INTRANET_Internal_Preview',
  SEASONAL_PREVIEW: 'INTRANET_Seasonal_Preview',
};

// LocalStorage keys
const LS_SELECTED_GROUP = 'intranet_selected_group';
const LS_AVAILABLE_GROUPS = 'intranet_available_groups';
const LS_EXPERIENCE = 'experience'; // 'internal' | 'seasonal'
const LS_PREVIEW = 'intranet_preview'; // 'true' | 'false'

// SessionStorage keys (guard against redirect storms)
const SS_REAUTH_GUARD = 'intranet_reauth_guard_v1';

// Mode definitions
const MODE_DEFS = [
  {
    group: GROUPS.INTERNAL_PROD,
    label: 'Internal (Production)',
    experience: 'internal',
    preview: false,
  },
  {
    group: GROUPS.SEASONAL_PROD,
    label: 'Seasonal (Production)',
    experience: 'seasonal',
    preview: false,
  },
  {
    group: GROUPS.INTERNAL_PREVIEW,
    label: 'Internal (Preview)',
    experience: 'internal',
    preview: true,
  },
  {
    group: GROUPS.SEASONAL_PREVIEW,
    label: 'Seasonal (Preview)',
    experience: 'seasonal',
    preview: true,
  },
];

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function getAllowedModeDefsFromGroups(groups) {
  const set = new Set(safeArray(groups));
  return MODE_DEFS.filter((m) => set.has(m.group));
}

function applySelectedGroup(group) {
  const def = MODE_DEFS.find((m) => m.group === group);
  if (!def) return;

  window.localStorage.setItem(LS_SELECTED_GROUP, def.group);
  window.localStorage.setItem(LS_EXPERIENCE, def.experience);
  window.localStorage.setItem(LS_PREVIEW, def.preview ? 'true' : 'false');
}

function readSelectedGroup() {
  return window.localStorage.getItem(LS_SELECTED_GROUP) || '';
}

function storeAvailableGroups(groups) {
  try {
    window.localStorage.setItem(LS_AVAILABLE_GROUPS, JSON.stringify(safeArray(groups)));
  } catch (e) {
    // ignore storage failures
  }
}

function isSandboxHost() {
  const h = (window.location.hostname || '').toLowerCase();
  return h.includes('preview') || h.includes('sandbox');
}

// Entry route: allows Okta tile to pick a mode then redirect to root
const Entry = ({ group }) => {
  applySelectedGroup(group);
  // Hard redirect to ensure the selection takes effect immediately
  window.location.replace('/');
  return null;
};

const Unauthorized = () => <div style={{ padding: 24 }}>Unauthorized</div>;
const CubsDotComRedirect = () => {
  window.location.replace('https://www.cubs.com/');
  return null;
};

// Mode picker shown only when user has >1 allowed mode and none is selected
const ModePicker = ({ modes }) => {
  const onPick = (group) => {
    applySelectedGroup(group);
    window.location.replace('/');
  };

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <h2>Select your intranet experience</h2>
      <p style={{ marginTop: 8 }}>
        Your account has access to multiple intranet experiences. Choose one to continue.
      </p>
      <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
        {modes.map((m) => (
          <button
            key={m.group}
            onClick={() => onPick(m.group)}
            style={{
              padding: '12px 14px',
              textAlign: 'left',
              borderRadius: 8,
              border: '1px solid #ddd',
              cursor: 'pointer',
              background: 'white',
            }}
          >
            <div style={{ fontWeight: 700 }}>{m.label}</div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              Group: {m.group} · Experience: {m.experience} · Preview: {String(m.preview)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ------------------------------
// ROUTE COMPONENTS
// ------------------------------
const HomeRoute = homeWithData(allNewsWithData(Home));
const CubsNewsRoute = withRouter(newsWithData(news));
const CubsNewsItemRoute = withRouter(newsChromeWithData(newsTemplateWithData(newsPages)));
const CubsNewsDeptRoute = withRouter(newsPagesWithData(newsPagesContentful));
const VideoSeriesRoute = withRouter(galleryWithData(gallery));
const DepartmentsRoute = withRouter(genericBoxWithData(genericBox));
const DepartmentRoute = withRouter(genericNewsWithData(genericNews));
const PoliciesRoute = withRouter(listWithData(list));
const ResourcesRoute = withRouter(genericBoxWithData(genericBox));
const SearchResultsRoute = withRouter(searchWithData(search));

const SignatureRoute = withRouter(signatureWithData(signature));
const AssociateRosterRoute = withRouter(listWithData(list));
const CalendarRoute = withRouter(calendarWithData(calendar));
const EmployeeBenefitsRoute = withRouter(galleryWithData(gallery));
const ExecutiveLeadershipRoute = withRouter(executiveWithData(executive));
const MarketingAssetsRoute = withRouter(listWithData(list));
const MediaCommsRoute = withRouter(listWithData(list));
const PublicPageRoute = withRouter(publicPageWithData(PublicPage));

class Router extends Component {
  state = {
    dataLoaded: false,
    oktaAuth: null,
    initError: null,

    groupsIntranet: [],
    authzReady: false,
  };

  // Retry guards (in-memory; resets on full reload)
  _didUserInfoRetry = false;
  _didTokenRenewRetry = false;

  // Prevent setState after unmount
  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;

    fetch('/authenticate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Failed to load Okta config: ${res.status} ${text}`);
        }
        return res.json();
      })
      .then((cfg) => {
        const issuer = cfg.issuer;
        const clientId = cfg.client_id;
        const redirectUri = cfg.redirect_uri || `${window.location.origin}/login/callback`;

        if (!issuer) throw new Error('No issuer returned from /authenticate');
        if (!clientId) throw new Error('No client_id returned from /authenticate');

        // NOTE: signInWithRedirect will use this issuer (e.g., https://cubs.okta.com/oauth2/default)
        // which is the correct/secure way to send users to Okta.
        const oktaAuth = new OktaAuth({ issuer, clientId, redirectUri });

        const triggerReauth = async (reason) => {
          try {
            // Prevent redirect storms: allow 1 reauth attempt per 30 seconds
            const now = Date.now();
            const guardRaw = window.sessionStorage.getItem(SS_REAUTH_GUARD);
            const guard = guardRaw ? JSON.parse(guardRaw) : null;

            if (guard && typeof guard.ts === 'number' && now - guard.ts < 30_000) {
              console.warn('[OKTA] Reauth suppressed (storm guard). Reason:', reason);
              // Fail closed: user cannot be authorized; show Unauthorized instead of blank/loop.
              if (this._isMounted) {
                this.setState({ groupsIntranet: [], authzReady: true });
              }
              this.props.history.replace('/unauthorized');
              return;
            }

            window.sessionStorage.setItem(SS_REAUTH_GUARD, JSON.stringify({ ts: now, reason }));

            // Clear local tokens to avoid immediately re-using a bad cached token set
            await oktaAuth.tokenManager.clear().catch(() => undefined);

            // Preserve the current route so URL entry works and tile entry continues to behave.
            // Okta SDK will store/restore originalUri as needed.
            const originalUri =
              window.location.pathname + window.location.search + window.location.hash;

            oktaAuth.setOriginalUri(originalUri);

            // Secure redirect through Okta /authorize endpoint
            await oktaAuth.signInWithRedirect();
          } catch (e) {
            console.error('[OKTA] Failed to trigger reauth:', e);
            // Last resort: route to unauthorized to avoid a blank screen
            this.props.history.replace('/unauthorized');
          }
        };

        const resolveGroups = (authState) => {
          const idClaims = authState?.idToken?.claims || {};
          const raw =
            idClaims.groups_intranet || idClaims.groupsIntranet || idClaims.groups || null;
          return safeArray(raw);
        };

        this._unsubscribeAuth = oktaAuth.authStateManager.subscribe(async (authState) => {
          try {
            if (!authState || !authState.isAuthenticated) {
              // Reset retry flags on logout / unauthenticated state
              this._didUserInfoRetry = false;
              this._didTokenRenewRetry = false;

              storeAvailableGroups([]);
              if (this._isMounted) {
                this.setState({ groupsIntranet: [], authzReady: true });
              }
              return;
            }

            // 1) Attempt from ID token claims first
            let groups = resolveGroups(authState);

            // Treat empty as suspicious (cached token missing claim or wrong token shape)
            if (groups.length === 0 && !this._didUserInfoRetry) {
              this._didUserInfoRetry = true;

              const user = await oktaAuth.getUser().catch(() => null);
              const userGroups =
                user?.groups_intranet || user?.groupsIntranet || user?.groups || null;
              groups = safeArray(userGroups);
            }

            // 2) Optional “hard retry”: silently renew tokens once, then re-evaluate
            if (groups.length === 0 && !this._didTokenRenewRetry) {
              this._didTokenRenewRetry = true;

              await Promise.allSettled([
                oktaAuth.tokenManager.renew('accessToken'),
                oktaAuth.tokenManager.renew('idToken'),
              ]);

              // Force authState recompute; subscriber will fire again
              await oktaAuth.authStateManager.updateAuthState();
              return;
            }

            // 3) Still empty => fail closed and reauth securely
            if (groups.length === 0) {
              console.warn('[OKTA] groups claim empty after retries; forcing reauth');
              await triggerReauth('groups_empty_after_retries');
              return;
            }

            console.log('[OKTA] resolved groups_intranet:', groups);

            // Persist available groups for Nav mode switcher
            const allowedModes = getAllowedModeDefsFromGroups(groups);
            storeAvailableGroups(allowedModes.map((m) => m.group));
            window.dispatchEvent(new Event('intranet_groups_updated'));

            // Determine selected group:
            const selected = readSelectedGroup();
            const selectedStillAllowed = allowedModes.some((m) => m.group === selected);

            if (selectedStillAllowed) {
              applySelectedGroup(selected);
            } else {
              // Default selection heuristics
              const wantPreview = isSandboxHost();
              const previewCandidate =
                allowedModes.find((m) => m.preview && m.experience === 'internal') ||
                allowedModes.find((m) => m.preview && m.experience === 'seasonal');

              const prodCandidate =
                allowedModes.find((m) => !m.preview && m.experience === 'internal') ||
                allowedModes.find((m) => !m.preview && m.experience === 'seasonal');

              const def = wantPreview
                ? previewCandidate || prodCandidate
                : prodCandidate || previewCandidate;

              if (def) applySelectedGroup(def.group);
              else {
                window.localStorage.removeItem(LS_SELECTED_GROUP);
              }
            }

            if (this._isMounted) {
              this.setState({ groupsIntranet: groups, authzReady: true });
            }
          } catch (e) {
            console.error('[OKTA] Authorization pipeline error:', e);
            // Fail closed with secure reauth; if that fails, go Unauthorized
            await triggerReauth('auth_pipeline_error');
          }
        });

        oktaAuth.start();
        oktaAuth.authStateManager.updateAuthState();

        if (this._isMounted) {
          this.setState({ dataLoaded: true, oktaAuth, initError: null });
        }
      })
      .catch((err) => {
        console.error('Failed to init Okta:', err);
        if (this._isMounted) {
          this.setState({ dataLoaded: true, oktaAuth: null, initError: err });
        }
      });
  }

  componentWillUnmount() {
    this._isMounted = false;

    if (this._unsubscribeAuth) {
      this._unsubscribeAuth();
      this._unsubscribeAuth = null;
    }
    if (this.state.oktaAuth) {
      this.state.oktaAuth.stop();
    }
  }

  restoreOriginalUri = async (_oktaAuth, originalUri) => {
    const relativeUri = toRelativeUrl(originalUri || '/', window.location.origin);
    window.location.replace(relativeUri);
  };

  isPublicMarkdownRoute = () => {
    const pathname = (this.props.location && this.props.location.pathname) || window.location.pathname || '';
    return /^\/public(?:\/|$)/.test(pathname);
  };

  render() {
    const { dataLoaded, oktaAuth, initError, authzReady, groupsIntranet } = this.state;

    const INTERNAL_GROUPS = [GROUPS.INTERNAL_PROD, GROUPS.INTERNAL_PREVIEW];
    const ANY_PROD_OR_PREVIEW = [
      GROUPS.INTERNAL_PROD,
      GROUPS.SEASONAL_PROD,
      GROUPS.INTERNAL_PREVIEW,
      GROUPS.SEASONAL_PREVIEW,
    ];

    const renderIfAllowed = (Cmp, requiredGroups) => (routeProps) => {
      const userGroups = safeArray(groupsIntranet);
      const req = safeArray(requiredGroups);
      const allowed = req.length === 0 ? true : req.some((g) => userGroups.includes(g));
      if (!allowed) return <Redirect to="/unauthorized" />;
      return <Cmp {...routeProps} />;
    };

    if (this.isPublicMarkdownRoute()) {
      return (
        <PageTransition>
          <Switch>
            <Route exact path="/public/:pageRoute?" component={PublicPageRoute} />
            <Route component={CubsDotComRedirect} />
          </Switch>
        </PageTransition>
      );
    }

    if (!dataLoaded) return <div style={{ padding: 24 }}>Loading…</div>;
    if (initError || !oktaAuth)
      return <div style={{ padding: 24 }}>Okta configuration unavailable.</div>;
    if (!authzReady) return <div style={{ padding: 24 }}>Authorizing…</div>;

    // Mode gate: if user has multiple modes and none selected, require a pick
    const allowedModes = getAllowedModeDefsFromGroups(groupsIntranet);
    const selected = readSelectedGroup();
    const selectedStillAllowed = allowedModes.some((m) => m.group === selected);
    const needsPick = allowedModes.length > 1 && !selectedStillAllowed;

    return (
      <PageTransition>
        <Switch>
          {/* Entry routes for Okta tiles (set mode then redirect home) */}
          <Route exact path="/entry/internal" render={() => <Entry group={GROUPS.INTERNAL_PROD} />} />
          <Route exact path="/entry/seasonal" render={() => <Entry group={GROUPS.SEASONAL_PROD} />} />
          <Route
            exact
            path="/entry/internal-preview"
            render={() => <Entry group={GROUPS.INTERNAL_PREVIEW} />}
          />
          <Route
            exact
            path="/entry/seasonal-preview"
            render={() => <Entry group={GROUPS.SEASONAL_PREVIEW} />}
          />

          {/* Everything else protected by Security */}
          <Route
            path="/"
            render={() => (
              <Security oktaAuth={oktaAuth} restoreOriginalUri={this.restoreOriginalUri}>
                {needsPick ? (
                  <ModePicker modes={allowedModes} />
                ) : (
                  <Switch>
                    <Route path="/login/callback" component={LoginCallback} />
                    <Route exact path="/unauthorized" component={Unauthorized} />

                    {/* Any valid intranet user (internal or seasonal, prod or preview) */}
                    <SecureRoute exact path="/" render={renderIfAllowed(HomeRoute, ANY_PROD_OR_PREVIEW)} />
                    <SecureRoute
                      exact
                      path="/cubs-news"
                      render={renderIfAllowed(CubsNewsRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/cubs-news/:news"
                      render={renderIfAllowed(CubsNewsItemRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/cubs-news-departments/:news"
                      render={renderIfAllowed(CubsNewsDeptRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/video-series"
                      render={renderIfAllowed(VideoSeriesRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/departments"
                      render={renderIfAllowed(DepartmentsRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/departments/:department"
                      render={renderIfAllowed(DepartmentRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/policies-procedures-and-workflows"
                      render={renderIfAllowed(PoliciesRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    <SecureRoute
                      exact
                      path="/resources"
                      render={renderIfAllowed(ResourcesRoute, ANY_PROD_OR_PREVIEW)}
                    />
                    {/* Internal-only features (internal prod OR internal preview) */}
                    <SecureRoute
                      exact
                      path="/signature"
                      render={renderIfAllowed(SignatureRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/associate-roster"
                      render={renderIfAllowed(AssociateRosterRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/calendar"
                      render={renderIfAllowed(CalendarRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/employee-benefits"
                      render={renderIfAllowed(EmployeeBenefitsRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/executive-leadership"
                      render={renderIfAllowed(ExecutiveLeadershipRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/marketing-and-promotional-assets"
                      render={renderIfAllowed(MarketingAssetsRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/media-and-communication-materials"
                      render={renderIfAllowed(MediaCommsRoute, INTERNAL_GROUPS)}
                    />
                    <SecureRoute
                      exact
                      path="/search-results"
                      render={renderIfAllowed(SearchResultsRoute, INTERNAL_GROUPS)}
                    />

                    {/* Catch-all */}
                    <SecureRoute path="*" render={() => <Redirect to="/" />} />
                  </Switch>
                )}
              </Security>
            )}
          />
        </Switch>
      </PageTransition>
    );
  }
}

export default Router;
